import { useEffect, useRef, useState } from 'react';
import { resolveUploadUrl } from '@/lib/uploads';
import { PasswordInput } from '@/components/PasswordInput';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Avatar,
  Spinner,
  Alert,
  AlertDescription,
} from '@klaro7/ui';
import { SPECIALIZATIONS } from '@klaro7/shared-types';

import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  useMyProfessionalProfile,
  useUpdateProfessionalProfile,
  useUploadAvatar,
  useChangePassword,
  useUpdateMyUserProfile,
  useSubmitForReview,
} from '../hooks/useProfessionalProfile';
import { extractErrorMessage } from '@/lib/api';
import { Link } from 'react-router-dom';
import { useProfessionalOnlineEligibility } from '../hooks/useProfessionalOnlineEligibility';
import { useProfessionalPresence } from '../hooks/useProfessionalPresence';
import { useProfessionalServiceChannels } from '../hooks/useProfessionalServiceChannels';
import { ProfileDescriptionEditor } from './ProfileDescriptionEditor';
import { SpecializationsInput } from './SpecializationsInput';
import { VideoPreview } from './VideoPreview';
import { PhoneChangeCard } from '@/features/phone-verification/components/PhoneChangeCard';
import { WorkingHoursNotice } from './WorkingHoursNotice';

type WorkingHour = {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
};

const DAYS = [
  'Domenica',
  'Lunedi',
  'Martedi',
  'Mercoledi',
  'Giovedi',
  'Venerdi',
  'Sabato',
] as const;

const DEFAULT_WORKING_HOUR: WorkingHour = {
  dayOfWeek: 1,
  startTime: '09:00',
  endTime: '18:00',
};

export function ConsultantProfilePage() {
  const { user, refreshUser } = useAuth();
  const { data: professional, isLoading, error } = useMyProfessionalProfile();
  const updateProfile = useUpdateProfessionalProfile();
  const uploadAvatar = useUploadAvatar();
  const changePassword = useChangePassword();
  const updateUserProfile = useUpdateMyUserProfile();
  const submitForReviewMutation = useSubmitForReview();
  const onlineEligibility = useProfessionalOnlineEligibility();
  const presence = useProfessionalPresence();
  const serviceChannels = useProfessionalServiceChannels();

  // Professional profile form state
  const [title, setTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [costPerMinute, setCostPerMinute] = useState<number | ''>(1.5);
  const [specializations, setSpecializations] = useState<string[]>([]);
  const [experience, setExperience] = useState<number | ''>('');
  const [certifications, setCertifications] = useState('');
  const [workingHours, setWorkingHours] = useState<WorkingHour[]>([]);
  const [hoursNotes, setHoursNotes] = useState('');
  const [timezone, setTimezone] = useState('Europe/Rome');

  // User profile form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [artFirstName, setArtFirstName] = useState('');
  const [artLastName, setArtLastName] = useState('');
  const [bio, setBio] = useState('');

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [hoursSuccess, setHoursSuccess] = useState('');
  const [hoursError, setHoursError] = useState('');

  // Messages
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [userProfileSuccess, setUserProfileSuccess] = useState('');
  const [userProfileError, setUserProfileError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const profileFormInitialized = useRef(false);
  const userFormInitialized = useRef(false);

  // Initialize once so background profile/cache updates cannot erase unsaved drafts.
  useEffect(() => {
    if (professional && !profileFormInitialized.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const prof = (professional as any).professional ?? professional;
      setTitle(prof.title || '');
      setVideoUrl(prof.videoUrl || '');
      setProfileDescription(prof.profileDescription || '');
      setCostPerMinute(prof.costPerMinute ?? 1.5);
      setSpecializations(
        (prof.specializations || [])
          .map((s: { name?: string } | string) => (typeof s === 'string' ? s : s.name ?? ''))
          .filter((s: string) => (SPECIALIZATIONS as readonly string[]).includes(s))
      );
      setExperience(prof.experience ?? '');
      setCertifications((prof.certifications || []).join(', '));
      setWorkingHours(
        [...(prof.availability?.workingHours || [])]
          .map((wh: WorkingHour) => ({
            dayOfWeek: wh.dayOfWeek,
            startTime: wh.startTime,
            endTime: wh.endTime,
          }))
          .sort((a: WorkingHour, b: WorkingHour) => a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime))
      );
      setTimezone(prof.availability?.timezone || 'Europe/Rome');
      setHoursNotes(prof.availability?.notes ?? '');
      profileFormInitialized.current = true;
    }
  }, [professional]);

  // ── Online eligibility ──────────────────────────────────────────────────────
  // A consultant can only go online once the fiscal profile is complete AND all
  // contractual documents are accepted. The backend enforces this too (it force-
  // offlines), but we mirror it here to disable the toggle and explain why.
  const hasActiveServiceChannel = Object.values(serviceChannels.channels).some(Boolean);
  const onlineRequirementsUnavailable =
    onlineEligibility.isLoading ||
    onlineEligibility.isError ||
    serviceChannels.isLoading ||
    serviceChannels.isError;
  const canGoOnline =
    !onlineRequirementsUnavailable &&
    onlineEligibility.canGoOnline &&
    hasActiveServiceChannel;
  const onlineStatus = presence.status;
  const isBusy = presence.confirmedStatus === 'busy';
  const serviceChannelsDisabled =
    serviceChannels.isLoading || serviceChannels.isError || serviceChannels.isUpdating;
  const missingRequirements = onlineRequirementsUnavailable
    ? []
    : hasActiveServiceChannel
      ? onlineEligibility.missingRequirements
      : [...onlineEligibility.missingRequirements, 'attiva almeno un canale di servizio'];

  useEffect(() => {
    if (user?.profile && !userFormInitialized.current) {
      setFirstName(user.profile.firstName || '');
      setLastName(user.profile.lastName || '');
      setArtFirstName(user.profile.artFirstName || '');
      setArtLastName(user.profile.artLastName || '');
      setBio(user.profile.bio || '');
      userFormInitialized.current = true;
    }
  }, [user]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (!profileSuccess) return;
    const t = setTimeout(() => setProfileSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [profileSuccess]);
  useEffect(() => {
    if (!userProfileSuccess) return;
    const t = setTimeout(() => setUserProfileSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [userProfileSuccess]);
  useEffect(() => {
    if (!hoursSuccess) return;
    const t = setTimeout(() => setHoursSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [hoursSuccess]);
  useEffect(() => {
    if (!passwordSuccess) return;
    const t = setTimeout(() => setPasswordSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [passwordSuccess]);

  const updateWorkingHour = (index: number, patch: Partial<WorkingHour>) => {
    setWorkingHours((current) =>
      current.map((wh, i) => (i === index ? { ...wh, ...patch } : wh))
    );
  };

  const addWorkingHour = () => {
    setWorkingHours((current) => [...current, { ...DEFAULT_WORKING_HOUR }]);
  };

  const removeWorkingHour = (index: number) => {
    setWorkingHours((current) => current.filter((_, i) => i !== index));
  };

  const handleSaveWorkingHours = async () => {
    setHoursError('');
    setHoursSuccess('');

    const cleanTimezone = timezone.trim();
    if (!cleanTimezone) {
      setHoursError('Il fuso orario è obbligatorio.');
      return;
    }

    const invalidRow = workingHours.find((wh) => !wh.startTime || !wh.endTime || wh.startTime >= wh.endTime);
    if (invalidRow) {
      setHoursError('Ogni fascia deve avere un orario di fine successivo all\'orario di inizio.');
      return;
    }

    const sortedWorkingHours = [...workingHours].sort(
      (a, b) => a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime)
    );

    try {
      await updateProfile.mutateAsync({
        availability: {
          isAvailable: onlineStatus !== 'offline',
          workingHours: sortedWorkingHours,
          timezone: cleanTimezone,
          notes: hoursNotes.trim() || null,
        },
      });
      setWorkingHours(sortedWorkingHours);
      setTimezone(cleanTimezone);
      setHoursSuccess('Orari aggiornati con successo');
    } catch (err) {
      setHoursError(extractErrorMessage(err));
    }
  };

  const handleSaveProfile = async () => {
    setProfileError('');
    setProfileSuccess('');
    try {
      await updateProfile.mutateAsync({
        title,
        // Send a trimmed string — an empty string explicitly clears the video.
        // `undefined` would be dropped from the payload and never remove it.
        videoUrl: videoUrl.trim(),
        profileDescription: profileDescription || undefined,
        costPerMinute: costPerMinute !== '' ? Number(costPerMinute) : undefined,
        specializations,
        experience: experience !== '' ? Number(experience) : undefined,
        certifications: certifications ? certifications.split(',').map((c) => c.trim()).filter(Boolean) : undefined,
      });
      setProfileSuccess('Profilo professionale aggiornato con successo');
    } catch (err) {
      setProfileError(extractErrorMessage(err));
    }
  };

  const handleSaveUserProfile = async () => {
    setUserProfileError('');
    setUserProfileSuccess('');
    try {
      await updateUserProfile.mutateAsync({ firstName, lastName, artFirstName, artLastName, bio });
      setUserProfileSuccess('Informazioni personali aggiornate con successo');
    } catch (err) {
      setUserProfileError(extractErrorMessage(err));
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarError('');
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setAvatarError('Il file è troppo grande. Il limite è 2 MB.');
      e.target.value = '';
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setAvatarError('Formato non supportato. Usa JPG, PNG, GIF o WebP.');
      e.target.value = '';
      return;
    }

    // Immediate optimistic preview while the upload is in flight.
    const objectUrl = URL.createObjectURL(file);
    setAvatarPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return objectUrl;
    });

    try {
      await uploadAvatar.mutateAsync(file);
      // Pull the new avatar path into the auth user so the canonical URL
      // replaces the blob preview everywhere it is read from `user.profile`.
      await refreshUser();
    } catch (err) {
      setAvatarError(extractErrorMessage(err));
      // Roll back preview on failure
      setAvatarPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }

    e.target.value = '';
  };

  // Clear the blob preview once the server-confirmed avatar is loaded.
  useEffect(() => {
    if (!avatarPreviewUrl) return;
    if (!user?.profile?.avatar) return;
    URL.revokeObjectURL(avatarPreviewUrl);
    setAvatarPreviewUrl(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profile?.avatar]);

  // Cleanup any dangling preview on unmount
  useEffect(() => {
    return () => {
      if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Le password non corrispondono');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('La nuova password deve avere almeno 8 caratteri');
      return;
    }

    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setPasswordSuccess('Password cambiata con successo');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(extractErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-10">
        <Alert variant="error">
          <AlertDescription>Errore nel caricamento del profilo: {extractErrorMessage(error)}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const fullName = `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`.trim();

  // Derive prof at render time for JSX access (mirrors useEffect extraction)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const prof = professional ? ((professional as any).professional ?? professional) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Profilo Professionista</h1>
        <p className="text-neutral-500">Gestisci il tuo profilo e le tue informazioni professionali</p>
      </div>

      {/* Stato pubblicazione profilo */}
      {prof?.verificationStatus !== 'verified' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          {prof?.verificationStatus === 'in_review' ? (
            <p className="text-sm text-amber-900">
              Profilo <strong>in revisione</strong>. Ti avviseremo appena approvato. Non è ancora visibile in piattaforma.
            </p>
          ) : (
            <>
              <p className="text-sm text-amber-900">
                Il tuo profilo <strong>non è ancora pubblicato</strong>. Completa i requisiti e invialo per la revisione.
              </p>
              {prof?.reviewNotes && (
                <div className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-2">
                  <p className="text-xs font-medium text-amber-700">Modifiche richieste dallo staff</p>
                  <p className="mt-0.5 text-sm text-amber-900">{prof.reviewNotes}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Checklist requisiti + bottone "Invia per revisione" */}
      {prof?.verificationStatus === 'pending' && (() => {
        const reviewMissing: string[] = [];
        if (!title || title === 'Da completare') reviewMissing.push('Titolo professionale');
        if (specializations.length === 0) reviewMissing.push('Almeno una specializzazione');
        if (!profileDescription || profileDescription.trim() === '') reviewMissing.push('Descrizione del profilo');
        if (costPerMinute === '') reviewMissing.push('Tariffa al minuto');
        if (!user?.profile?.avatar) reviewMissing.push('Foto profilo');
        reviewMissing.push(
          ...missingRequirements.map(
            (requirement) => requirement.charAt(0).toUpperCase() + requirement.slice(1)
          )
        );
        return (
          <div className="rounded-xl border border-neutral-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-neutral-900">Pronto per la pubblicazione?</h3>
            {reviewMissing.length > 0 ? (
              <ul className="mt-2 list-disc pl-5 text-sm text-neutral-600">
                {reviewMissing.map((m) => <li key={m}>{m}</li>)}
              </ul>
            ) : onlineEligibility.isLoading || serviceChannels.isLoading ? (
              <p className="mt-2 text-sm text-neutral-600" role="status">
                Verifica dei requisiti in corso...
              </p>
            ) : onlineEligibility.isError || serviceChannels.isError ? (
              <p className="mt-2 text-sm text-red-600" role="status">
                Impossibile verificare i requisiti. Riprova tra poco.
              </p>
            ) : (
              <p className="mt-2 text-sm text-emerald-600">Tutti i requisiti sono soddisfatti.</p>
            )}
            {reviewMissing.length > 0 && onlineRequirementsUnavailable && (
              <p
                className={`mt-2 text-sm ${
                  onlineEligibility.isError || serviceChannels.isError
                    ? 'text-red-600'
                    : 'text-neutral-600'
                }`}
                role="status"
              >
                {onlineEligibility.isError || serviceChannels.isError
                  ? 'Impossibile verificare tutti i requisiti. Riprova tra poco.'
                  : 'Verifica degli altri requisiti in corso...'}
              </p>
            )}
            <button
              onClick={() => submitForReviewMutation.mutate()}
              disabled={
                reviewMissing.length > 0 ||
                onlineRequirementsUnavailable ||
                submitForReviewMutation.isPending
              }
              className="mt-3 rounded-lg bg-brand-orange px-4 py-2 text-sm font-semibold text-white hover:bg-brand-orange/90 disabled:opacity-50"
            >
              Invia per revisione
            </button>
          </div>
        );
      })()}

      {/* Online Status Toggle */}
      <div className={`flex items-center justify-between rounded-xl border p-5 transition-colors ${
        onlineStatus === 'online'
          ? 'border-emerald-200 bg-emerald-50'
          : onlineStatus === 'busy'
            ? 'border-amber-200 bg-amber-50'
          : onlineStatus === 'paused'
            ? 'border-orange-200 bg-orange-50'
            : 'border-neutral-200 bg-white'
      }`}>
        <div className="flex min-w-0 items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
            onlineStatus === 'online'
              ? 'bg-emerald-100'
              : onlineStatus === 'busy'
                ? 'bg-amber-100'
                : onlineStatus === 'paused'
                  ? 'bg-brand-orange/10'
                  : 'bg-neutral-100'
          }`}>
            <span className={`h-3 w-3 rounded-full ${
              onlineStatus === 'online'
                ? 'motion-safe:animate-pulse bg-emerald-500'
                : onlineStatus === 'busy'
                  ? 'bg-amber-500'
                  : onlineStatus === 'paused'
                    ? 'bg-brand-orange'
                    : 'bg-neutral-400'
            }`} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-900">
              {presence.isLoading
                ? 'Caricamento disponibilità...'
                : presence.isError
                  ? 'Disponibilità non disponibile'
                  : onlineStatus === 'online'
                    ? 'Sei Online'
                    : onlineStatus === 'busy'
                      ? 'Sei in chiamata'
                      : onlineStatus === 'paused'
                        ? 'Sei in Pausa'
                        : 'Sei Offline'}
            </p>
            <p className="text-xs text-neutral-500">
              {onlineStatus === 'online'
                ? 'I clienti possono contattarti sui canali attivi'
                : onlineStatus === 'busy'
                  ? 'Potrai cambiare disponibilità al termine della sessione'
                : onlineStatus === 'paused'
                  ? 'I clienti ti vedono in pausa e non possono contattarti. Tocca l\'interruttore per tornare online'
                  : 'Non sei visibile come disponibile ai clienti'}
            </p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          // In pausa l'interruttore è SPENTO e riaccenderlo riprende (online):
          // prima appariva acceso e toccarlo mandava offline, così chi era in
          // pausa credeva di essere raggiungibile e restava bloccato.
          aria-label={
            onlineStatus === 'offline'
              ? 'Vai online'
              : onlineStatus === 'paused'
                ? 'Riprendi: torna online'
                : 'Vai offline'
          }
          aria-checked={onlineStatus === 'online' || onlineStatus === 'busy'}
          onClick={() =>
            presence.setStatus(
              onlineStatus === 'offline' || onlineStatus === 'paused' ? 'online' : 'offline'
            )
          }
          disabled={
            presence.isUpdating ||
            presence.isLoading ||
            presence.isError ||
            !presence.isConnected ||
            isBusy ||
            (!canGoOnline && (onlineStatus === 'offline' || onlineStatus === 'paused'))
          }
          title={
            (onlineStatus === 'offline' || onlineStatus === 'paused') && !canGoOnline
              ? onlineRequirementsUnavailable
                ? 'Verifica dei requisiti non disponibile'
                : 'Completa i requisiti per andare online'
              : undefined
          }
          className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
            onlineStatus === 'online'
              ? 'bg-emerald-500'
              : onlineStatus === 'busy'
                ? 'bg-amber-500'
                : onlineStatus === 'paused'
                  ? 'bg-brand-orange'
                  : 'bg-neutral-300'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              onlineStatus === 'online' || onlineStatus === 'busy' ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {onlineStatus === 'offline' && onlineRequirementsUnavailable && (
        <div
          className={`rounded-xl border px-5 py-4 text-sm ${
            onlineEligibility.isError || serviceChannels.isError
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-neutral-200 bg-neutral-50 text-neutral-600'
          }`}
          role="status"
        >
          {onlineEligibility.isError || serviceChannels.isError
            ? 'Impossibile verificare i requisiti per andare online. Riprova tra poco.'
            : 'Verifica dei requisiti per andare online in corso...'}
        </div>
      )}

      {/* Eligibility warning — blocks going online until requirements are met */}
      {!onlineRequirementsUnavailable && !canGoOnline && onlineStatus === 'offline' && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
          <svg className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <div className="text-sm">
            <p className="font-semibold text-amber-800">Non puoi ancora andare online</p>
            <p className="mt-0.5 text-amber-700">
              Per metterti online e ricevere richieste devi prima:
            </p>
            <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-amber-700">
              {missingRequirements.map((req) => (
                <li key={req}>{req}</li>
              ))}
            </ul>
            <Link
              to="/dashboard/profilo-fiscale"
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-amber-800 underline hover:text-amber-900"
            >
              Vai al profilo fiscale e ai contratti
            </Link>
          </div>
        </div>
      )}

      {/* Working Hours */}
      <Card>
        <CardHeader>
          <CardTitle>Orari di lavoro</CardTitle>
          <CardDescription>Imposta le fasce orarie mostrate nel profilo pubblico</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <WorkingHoursNotice variant="consultant" />
          <div className="flex flex-col gap-3">
            {workingHours.length === 0 ? (
              <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-4 py-5 text-sm text-neutral-500">
                Nessuna fascia inserita.
              </div>
            ) : (
              workingHours.map((wh, index) => (
                <div key={index} className="grid gap-3 rounded-lg border border-neutral-200 p-3 sm:grid-cols-[1.3fr_1fr_1fr_auto] sm:items-end">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-neutral-700">Giorno</label>
                    <select
                      value={wh.dayOfWeek}
                      onChange={(e) => updateWorkingHour(index, { dayOfWeek: Number(e.target.value) })}
                      className="h-10 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {DAYS.map((day, dayIndex) => (
                        <option key={day} value={dayIndex}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Input
                    label="Dalle"
                    type="time"
                    value={wh.startTime}
                    onChange={(e) => updateWorkingHour(index, { startTime: e.target.value })}
                  />

                  <Input
                    label="Alle"
                    type="time"
                    value={wh.endTime}
                    onChange={(e) => updateWorkingHour(index, { endTime: e.target.value })}
                    error={wh.startTime >= wh.endTime ? 'Orario non valido' : undefined}
                  />

                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => removeWorkingHour(index)}
                    aria-label="Rimuovi fascia oraria"
                    title="Rimuovi fascia oraria"
                  >
                    <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </Button>
                </div>
              ))
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <Input
              label="Fuso orario"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="Europe/Rome"
            />
            <Button type="button" variant="outline" onClick={addWorkingHour}>
              <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Aggiungi fascia
            </Button>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700" htmlFor="hoursNotes">
              Note sugli orari
            </label>
            <textarea
              id="hoursNotes"
              value={hoursNotes}
              onChange={(e) => setHoursNotes(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder='es. "Il giovedì rispondo solo in chat", "Ad agosto sono assente"'
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-neutral-500">
              Visibile nel tuo profilo pubblico sotto gli orari. {hoursNotes.length}/300
            </p>
          </div>

          {hoursSuccess && (
            <Alert variant="success">
              <AlertDescription>{hoursSuccess}</AlertDescription>
            </Alert>
          )}
          {hoursError && (
            <Alert variant="error">
              <AlertDescription>{hoursError}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveWorkingHours}
              loading={updateProfile.isPending}
              className="bg-brand-orange hover:bg-brand-orange/90"
            >
              Salva Orari
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Avatar Section */}
      <Card>
        <CardHeader>
          <CardTitle>Foto Profilo</CardTitle>
          <CardDescription>La tua foto sarà visibile ai clienti nella piattaforma</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <Avatar
              src={avatarPreviewUrl ?? resolveUploadUrl(user?.profile?.avatar)}
              fallback={fullName}
              size="xl"
              className="h-24 w-24 text-2xl"
            />
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                loading={uploadAvatar.isPending}
              >
                Carica nuova foto
              </Button>
              <p className="text-xs text-neutral-500">JPG, PNG, GIF o WebP. Max 2 MB.</p>
              {avatarError && <p className="text-xs text-red-600">{avatarError}</p>}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleAvatarChange}
            className="hidden"
          />
        </CardContent>
      </Card>

      {/* Service Channels */}
      <Card>
        <CardHeader>
          <CardTitle>Canali di Servizio</CardTitle>
          <CardDescription>Scegli quali servizi offrire ai tuoi clienti. Le opzioni disattivate non saranno visibili nel tuo profilo pubblico.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="space-y-3"
            aria-busy={serviceChannels.isLoading || serviceChannels.isUpdating}
          >
            {/* Chat toggle */}
            <label className={`flex items-center justify-between rounded-lg border border-neutral-200 p-4 transition-colors ${
              serviceChannelsDisabled
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer hover:bg-neutral-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10">
                  <svg className="h-5 w-5 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Chat</p>
                  <p className="text-sm text-neutral-500">Consenti ai clienti di contattarti via chat</p>
                </div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Abilita Chat"
                  checked={serviceChannels.channels.chat}
                  disabled={serviceChannelsDisabled}
                  onChange={(e) => serviceChannels.setChannel('chat', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="h-6 w-11 rounded-full bg-neutral-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-teal peer-checked:after:translate-x-full peer-focus-visible:ring-2 peer-focus-visible:ring-brand-teal peer-focus-visible:ring-offset-2" />
              </div>
            </label>

            {/* Audio Call toggle */}
            <label className={`flex items-center justify-between rounded-lg border border-neutral-200 p-4 transition-colors ${
              serviceChannelsDisabled
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer hover:bg-neutral-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50">
                  <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Chiamata Audio</p>
                  <p className="text-sm text-neutral-500">Consenti ai clienti di chiamarti in audio</p>
                </div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Abilita Chiamata Audio"
                  checked={serviceChannels.channels.audioCall}
                  disabled={serviceChannelsDisabled}
                  onChange={(e) => serviceChannels.setChannel('audioCall', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="h-6 w-11 rounded-full bg-neutral-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-teal peer-checked:after:translate-x-full peer-focus-visible:ring-2 peer-focus-visible:ring-brand-teal peer-focus-visible:ring-offset-2" />
              </div>
            </label>

            {/* Video Call toggle */}
            <label className={`flex items-center justify-between rounded-lg border border-neutral-200 p-4 transition-colors ${
              serviceChannelsDisabled
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer hover:bg-neutral-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10">
                  <svg className="h-5 w-5 text-brand-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Videochiamata</p>
                  <p className="text-sm text-neutral-500">Consenti ai clienti di videochiamarti</p>
                </div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Abilita Videochiamata"
                  checked={serviceChannels.channels.videoCall}
                  disabled={serviceChannelsDisabled}
                  onChange={(e) => serviceChannels.setChannel('videoCall', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="h-6 w-11 rounded-full bg-neutral-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-teal peer-checked:after:translate-x-full peer-focus-visible:ring-2 peer-focus-visible:ring-brand-teal peer-focus-visible:ring-offset-2" />
              </div>
            </label>

            {/* Availability pause — visible "In pausa" (orange dot), not contactable */}
            <div className={`flex items-center justify-between rounded-lg border p-4 transition-colors ${
              onlineStatus === 'paused' ? 'border-orange-200 bg-orange-50' : 'border-neutral-200'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  onlineStatus === 'paused' ? 'bg-brand-orange/20' : 'bg-brand-orange/10'
                }`}>
                  <svg className="h-5 w-5 text-brand-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-neutral-900">Pausa</p>
                  <p className="text-sm text-neutral-500">
                    {onlineStatus === 'paused'
                      ? 'Sei in pausa: i clienti ti vedono ma non possono contattarti'
                      : 'Sospendi temporaneamente chiamate e chat restando visibile'}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                onClick={() => presence.setStatus(onlineStatus === 'paused' ? 'online' : 'paused')}
                disabled={
                  onlineStatus === 'offline' ||
                  isBusy ||
                  presence.isUpdating ||
                  presence.isLoading ||
                  presence.isError ||
                  !presence.isConnected ||
                  (onlineStatus === 'paused' && !canGoOnline)
                }
                title={
                  onlineStatus === 'offline'
                    ? 'Vai online per poterti mettere in pausa'
                    : onlineStatus === 'paused' && !canGoOnline
                      ? 'Attiva un canale e completa i requisiti per riprendere'
                      : undefined
                }
                className={onlineStatus === 'paused' ? 'bg-brand-teal hover:bg-brand-teal/90' : 'bg-brand-orange hover:bg-brand-orange/90'}
              >
                {onlineStatus === 'paused' ? 'Riprendi' : 'Pausa'}
              </Button>
            </div>
          </div>

          <p
            className="min-h-5 text-right text-xs text-neutral-500"
            aria-live="polite"
          >
            {serviceChannels.isUpdating
              ? 'Salvataggio...'
              : serviceChannels.isLoading
                ? 'Caricamento...'
                : serviceChannels.isError
                  ? 'Canali non disponibili'
              : serviceChannels.saved
                ? 'Salvato'
                : ''}
          </p>
        </CardContent>
      </Card>

      {/* Video Introduction */}
      <Card>
        <CardHeader>
          <CardTitle>Video di Presentazione</CardTitle>
          <CardDescription>
            Aggiungi un video per presentarti ai potenziali clienti. Usa un link YouTube o Vimeo.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
          />
          <VideoPreview url={videoUrl} />
        </CardContent>
      </Card>

      {/* Profile Description (WYSIWYG) */}
      <Card>
        <CardHeader>
          <CardTitle>Descrizione Profilo</CardTitle>
          <CardDescription>
            Descrivi la tua esperienza, il tuo approccio e cosa offri ai tuoi clienti. Questo testo sarà visibile nella tua pagina pubblica.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ProfileDescriptionEditor
            value={profileDescription}
            onChange={setProfileDescription}
          />
        </CardContent>
      </Card>

      {/* Cost, Specializations, Experience */}
      <Card>
        <CardHeader>
          <CardTitle>Dettagli Professionali</CardTitle>
          <CardDescription>Configura le tue tariffe, specializzazioni e competenze</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Title */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Titolo Professionale</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="es. Cartomante Esperta, Astrologa Professionista"
            />
          </div>

          {/* Cost Per Minute */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Costo al Minuto (EUR)</label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={costPerMinute}
                onChange={(e) => setCostPerMinute(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="1.50"
                min={1.5}
                max={10}
                step={0.1}
                className="max-w-[200px]"
              />
              <span className="text-sm text-neutral-500">EUR/min</span>
            </div>
            <p className="mt-1 text-xs text-neutral-500">Minimo 1.50 EUR - Massimo 10.00 EUR</p>
            {costPerMinute !== '' && (typeof costPerMinute !== 'number' || costPerMinute < 1.5 || costPerMinute > 10) && (
              <p className="mt-1 text-xs text-red-600">Il costo deve essere tra 1.50 e 10.00 EUR</p>
            )}
          </div>

          {/* Specializations */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Specializzazioni</label>
            <SpecializationsInput
              value={specializations}
              onChange={setSpecializations}
            />
            {specializations.length === 0 && (
              <p className="mt-1 text-xs text-red-600">Seleziona almeno una specializzazione</p>
            )}
          </div>

          {/* Experience */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Anni di Esperienza</label>
            <Input
              type="number"
              value={experience}
              onChange={(e) => setExperience(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="0"
              min={0}
              className="max-w-[200px]"
            />
          </div>

          {/* Certifications */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Certificazioni</label>
            <Input
              value={certifications}
              onChange={(e) => setCertifications(e.target.value)}
              placeholder="Separa con virgole, es. Certificazione AIDA, Diploma Astrologia"
            />
            <p className="mt-1 text-xs text-neutral-500">Separa le certificazioni con una virgola</p>
          </div>

          {/* Save Professional Profile */}
          {profileSuccess && (
            <Alert variant="success">
              <AlertDescription>{profileSuccess}</AlertDescription>
            </Alert>
          )}
          {profileError && (
            <Alert variant="error">
              <AlertDescription>{profileError}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveProfile}
              loading={updateProfile.isPending}
              className="bg-brand-orange hover:bg-brand-orange/90"
            >
              Salva Profilo Professionale
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informazioni Personali</CardTitle>
          <CardDescription>I tuoi dati personali di base</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Nome</label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Nome"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Cognome</label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Cognome"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Nome d'arte</label>
              <Input
                value={artFirstName}
                onChange={(e) => setArtFirstName(e.target.value)}
                placeholder="Nome d'arte"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Cognome d'arte</label>
              <Input
                value={artLastName}
                onChange={(e) => setArtLastName(e.target.value)}
                placeholder="Cognome d'arte"
              />
            </div>
          </div>
          <div>
            <PhoneChangeCard
              phone={user?.profile?.phone}
              phoneVerified={user?.profile?.phoneVerified}
              pendingPhone={user?.profile?.pendingPhone}
              onChanged={() => { void refreshUser(); }}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Bio</label>
            <Input
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Breve bio..."
            />
          </div>

          {userProfileSuccess && (
            <Alert variant="success">
              <AlertDescription>{userProfileSuccess}</AlertDescription>
            </Alert>
          )}
          {userProfileError && (
            <Alert variant="error">
              <AlertDescription>{userProfileError}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveUserProfile}
              loading={updateUserProfile.isPending}
              className="bg-brand-orange hover:bg-brand-orange/90"
            >
              Salva Informazioni Personali
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card>
        <CardHeader>
          <CardTitle>Cambio Password</CardTitle>
          <CardDescription>Modifica la password del tuo account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-neutral-700">Password Attuale</label>
            <PasswordInput
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Password attuale"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Nuova Password</label>
              <PasswordInput
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nuova password"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">Conferma Password</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Conferma password"
              />
            </div>
          </div>

          {passwordSuccess && (
            <Alert variant="success">
              <AlertDescription>{passwordSuccess}</AlertDescription>
            </Alert>
          )}
          {passwordError && (
            <Alert variant="error">
              <AlertDescription>{passwordError}</AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <Button
              onClick={handleChangePassword}
              loading={changePassword.isPending}
              disabled={!currentPassword || !newPassword || !confirmPassword}
              className="bg-brand-orange hover:bg-brand-orange/90"
            >
              Cambia Password
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
